import { DressingWeight, type MixedDressingDetails } from '@sg/graphql-schema';

import { type Modification, type ModificationsMap } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

export function generateMixedDressingDetailsFactory(params: Params) {
  const {
    hasMultipleMixableDressings,
    currentMixedDressingDetails,
    includedModificationsMap,
    extrasModificationsMap,
    defaultWeight,
  } = params;

  const defaultDressingWeight = getDefaultDressingWeight(defaultWeight, hasMultipleMixableDressings); // prettier-ignore

  return (modification: Modification) => {
    const { ingredient, ingredientId } = modification;

    if (!ingredient) {
      return null;
    }

    const exactDressingDetail = currentMixedDressingDetails.find(
      (dressingDetail) => dressingDetail.ingredientId === ingredient.id,
    );

    if (exactDressingDetail) {
      return exactDressingDetail;
    }

    // NOTE: Included slot optimization can move a dressing between its included and
    //       extras counterparts, which changes the encoded ingredient id. A detail saved
    //       under the counterpart id still belongs to this dressing.
    const counterpartIngredientIds = new Set([
      includedModificationsMap.get(ingredientId ?? '')?.ingredient?.id,
      extrasModificationsMap.get(ingredientId ?? '')?.ingredient?.id,
    ]);

    const counterpartDressingDetail = currentMixedDressingDetails.find((dressingDetail) =>
      counterpartIngredientIds.has(dressingDetail.ingredientId),
    );

    if (counterpartDressingDetail) {
      return { ...counterpartDressingDetail, ingredientId: ingredient.id };
    }

    return generateMixedDressingDetails(modification, defaultDressingWeight);
  };
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the default dressing weight to use when generating mixed dressing
 * details.
 *
 * - If a default weight is provided, it will be used.
 * - If there are multiple mixable dressings, the default weight will be "light".
 * - If there is only one mixable dressing, the default weight will be "medium".
 */
function getDefaultDressingWeight(
  defaultWeight: DressingWeight | undefined,
  hasMultipleMixableDressings: boolean,
) {
  if (defaultWeight) {
    return defaultWeight;
  }

  if (hasMultipleMixableDressings) {
    return DressingWeight.Light;
  }

  return DressingWeight.Medium;
}

function generateMixedDressingDetails(
  modification: Modification,
  defaultWeight: DressingWeight,
): MixedDressingDetails | null {
  const { ingredient } = modification;

  if (!ingredient) {
    return null;
  }

  return { ingredientId: ingredient.id, weight: defaultWeight };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  hasMultipleMixableDressings: boolean;
  currentMixedDressingDetails: MixedDressingDetails[];
  includedModificationsMap: ModificationsMap;
  extrasModificationsMap: ModificationsMap;
  defaultWeight?: DressingWeight;
};
